import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCollection } from "../useData";
import { resolveImg } from "../api";
import { useCart } from "../context/CartContext";
import SEO from "../components/SEO";
import NotFound from "../components/NotFound";

const ShopDetail = () => {
  const { id } = useParams();
  const shopProducts = useCollection("shop_products") || [];
  const product = shopProducts.find((p) => p.id === id);
  const { add, setOpen } = useCart();
  const [qty, setQty] = useState(1);

  if (!shopProducts.length) return null;
  if (!product) return <NotFound section="This item" backTo="/shop" backLabel="Back to shop" />;

  const price = typeof product.price === "number" ? product.price : 0;
  const details = Array.isArray(product.details) ? product.details : [];
  const related = shopProducts.filter((p) => p.id !== product.id).slice(0, 3);

  return (
    <div className="px-6 md:px-10 max-w-[1100px] mx-auto">
      <SEO
        title={product.title}
        description={product.description || product.subtitle || `${product.title} — book by Alice Lisi.`}
        image={product.cover}
        path={`/shop/${product.id}`}
      />
      <div className="pt-2 pb-6 flex items-baseline justify-between">
        <Link to="/shop" className="font-serif tlink tlink-mute">← Shop</Link>
        <span className="font-serif text-[color:var(--ink-mute)]">Book — {product.year}</span>
      </div>

      <div className="grid grid-cols-12 gap-8 md:gap-16 py-4">
        <div className="col-span-12 md:col-span-7">
          <div className="bg-[color:var(--paper-2)] aspect-square overflow-hidden flex items-center justify-center">
            {product.cover ? (
              <img src={resolveImg(product.cover)} alt={product.title} className="w-full h-full object-cover" />
            ) : null}
          </div>
        </div>

        <div className="col-span-12 md:col-span-5">
          <p className="font-serif text-[color:var(--ink-mute)]">{product.author}</p>
          <h1 className="font-serif italic text-5xl md:text-6xl leading-[1] mt-2">{product.title}</h1>
          <p className="font-serif text-xl text-[color:var(--ink-mute)] mt-2">{product.subtitle}</p>

          <p className="font-serif text-xl mt-8 leading-[1.45]">{product.description}</p>

          <div className="mt-10 flex items-baseline justify-between border-t border-[color:var(--line)] pt-5">
            <span className="font-serif italic text-[color:var(--ink-mute)]">Price</span>
            <span className="font-serif text-3xl counter">€{price.toFixed(2)}</span>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="inline-flex items-center border border-[color:var(--ink)]">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3 font-serif">−</button>
              <span className="px-4 font-serif counter">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3 py-3 font-serif">+</button>
            </div>
            <button onClick={() => { add(product, qty); setOpen(true); }} className="btn-sober flex-1 justify-center">Add to cart</button>
          </div>

          <div className="mt-10 border-t border-[color:var(--line)] pt-5">
            <p className="font-serif italic text-[color:var(--ink-mute)] mb-3">Details</p>
            {details.length ? (
              <ul className="space-y-1.5">
                {details.map((d, i) => (
                  <li key={i} className="font-serif text-lg leading-tight">— {d}</li>
                ))}
              </ul>
            ) : (
              <p className="font-serif text-[color:var(--ink-mute)] italic">Details coming soon.</p>
            )}
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 font-serif text-[color:var(--ink-mute)] border-t border-[color:var(--line)] pt-5">
            <div>Ships 3–5 days</div>
            <div>Secure checkout</div>
            <div>14-day returns</div>
          </div>
        </div>
      </div>

      {/* Related */}
      <div className="mt-24 border-t border-[color:var(--line)] pt-10">
        <div className="flex items-baseline justify-between mb-8">
          <span className="font-serif italic text-[color:var(--ink-mute)]">Also from the shop</span>
          <Link to="/shop" className="font-serif italic tlink">All items →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-10">
          {related.map((p) => (
            <Link to={`/shop/${p.id}`} key={p.id} className="tile">
              <div className="aspect-square bg-[color:var(--paper-2)] overflow-hidden flex items-center justify-center">
                {p.cover ? (
                  <img src={resolveImg(p.cover)} alt={p.title} className="tile-img max-w-full max-h-full object-contain" />
                ) : null}
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <p className="font-serif italic">{p.title}</p>
                <span className="font-serif counter">€{typeof p.price === "number" ? p.price.toFixed(2) : "—"}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShopDetail;
